import React, { useMemo } from 'react';
import { FiChevronDown, FiAlertCircle } from 'react-icons/fi';
import styles from '../Auth.module.css';

/**
 * HierarchicalFieldSelect - 3-level cascading dropdown for academic fields
 * Category → Field → SubField
 */
const HierarchicalFieldSelect = ({
  fieldsData,
  fieldCategory,
  field,
  subField,
  onCategoryChange,
  onFieldChange,
  onSubFieldChange,
  isCustomField,
  onCustomFieldToggle,
  customFieldValue,
  onCustomFieldChange,
  error,
  disabled = false,
}) => {
  // Get available fields based on selected category
  const availableFields = useMemo(() => {
    if (!fieldCategory || !fieldsData?.fields) return [];
    const categoryData = fieldsData.fields[fieldCategory];
    return categoryData?.fields || [];
  }, [fieldCategory, fieldsData]);

  // Get available subfields based on selected field
  const availableSubFields = useMemo(() => {
    if (!field || !availableFields.length) return [];
    const selectedField = availableFields.find(f => f.name === field);
    return selectedField?.subFields || [];
  }, [field, availableFields]);

  // Handle category change - reset field and subfield
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    onCategoryChange(value);
    onFieldChange('');
    onSubFieldChange('');
  };

  // Handle field change - reset subfield
  const handleFieldChange = (e) => {
    const value = e.target.value;
    onFieldChange(value);
    onSubFieldChange('');
  };

  if (isCustomField) {
    return (
      <div className={styles['au-hierarchical-field']}>
        <div className={styles['au-input-group']}>
          <label className={styles['au-input-group__label']}>Your Field of Study</label>
          <input
            type="text"
            value={customFieldValue || ''}
            onChange={(e) => onCustomFieldChange(e.target.value)}
            placeholder="Enter your field of study"
            className={styles['au-input']}
            disabled={disabled}
          />
        </div>
        
        <label className={styles['au-checkbox']} style={{ marginTop: '0.75rem' }}>
          <input
            type="checkbox"
            className={styles['au-checkbox__input']}
            checked={isCustomField}
            onChange={(e) => onCustomFieldToggle(e.target.checked)}
            disabled={disabled}
          />
          <span className={styles['au-checkbox__label']}>
            My field is not listed
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className={styles['au-hierarchical-field']}>
      {/* Category Select */}
      <div className={styles['au-input-group']}>
        <label className={styles['au-input-group__label']}>Category</label>
        <div className={styles['au-select-wrapper']}>
          <select
            value={fieldCategory}
            onChange={handleCategoryChange}
            className={styles['au-select']}
            disabled={disabled}
          >
            <option value="">Select a category</option>
            {fieldsData?.categories && Object.entries(fieldsData.categories).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <FiChevronDown className={styles['au-select-wrapper__icon']} />
        </div>
      </div>

      {/* Field Select */}
      <div className={styles['au-input-group']}>
        <label className={styles['au-input-group__label']}>Field</label>
        <div className={styles['au-select-wrapper']}>
          <select
            value={field}
            onChange={handleFieldChange}
            className={styles['au-select']}
            disabled={disabled || !fieldCategory}
          >
            <option value="">
              {fieldCategory ? 'Select your field' : 'Select a category first'}
            </option>
            {availableFields.map((f) => (
              <option key={f.name} value={f.name}>{f.name}</option>
            ))}
          </select>
          <FiChevronDown className={styles['au-select-wrapper__icon']} />
        </div>
      </div>

      {/* SubField Select (optional) */}
      {availableSubFields.length > 0 && (
        <div className={styles['au-input-group']}>
          <label className={styles['au-input-group__label']}>
            Specialization <span className={styles['au-input-group__optional']}>(Optional)</span>
          </label>
          <div className={styles['au-select-wrapper']}>
            <select
              value={subField}
              onChange={(e) => onSubFieldChange(e.target.value)}
              className={styles['au-select']}
              disabled={disabled}
            >
              <option value="">Select a specialization</option>
              {availableSubFields.map((sf) => (
                <option key={sf} value={sf}>{sf}</option>
              ))}
            </select>
            <FiChevronDown className={styles['au-select-wrapper__icon']} />
          </div>
        </div>
      )}

      {/* Custom field toggle */}
      <label className={styles['au-checkbox']} style={{ marginTop: '0.75rem' }}>
        <input
          type="checkbox"
          className={styles['au-checkbox__input']}
          checked={isCustomField}
          onChange={(e) => onCustomFieldToggle(e.target.checked)}
          disabled={disabled}
        />
        <span className={styles['au-checkbox__label']}>
          My field is not listed
        </span>
      </label>

      {/* Error display */}
      {error && (
        <div className={styles['au-input-group__error']}>
          <FiAlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default HierarchicalFieldSelect;
